import axios from 'axios';
import { ActionTree, Module, MutationTree } from 'vuex';
import { namespace } from 'vuex-class';

import { RootState } from './index';


export interface UserLoginCredentials {
  password: string;
  username: string;
}

export interface UserChangePasswordCredentials {
  newPassword: string;
  oldPassword: string;
  username: string;
}

export interface UserResetPasswordCredentials {
  newPassword: string;
  username: string;
  verificationCode: string;
}

export interface UserLoginResponse {
  token: string;
  user: User;
}

export interface User {
  username: string;
  userInfo: UserInfo;
}

export interface UserInfo {
  companyId: string;
  email: string;
}

const saveToLocalStorage = (settings: UserLoginResponse) => {
  if (settings) {
    localStorage.setItem('wra-sir-settings', JSON.stringify(settings));
  }
};

export const loadFromLocalStorage = (): UserLoginResponse | null => {
  try {
    const settings = localStorage.getItem('wra-sir-settings');
    const value = settings && JSON.parse(settings);
    return value;
  }
  catch {
    return null;
  }
};

const deleteFromLocalStorage = () => {
  localStorage.removeItem('wra-sir-settings');
};


/**
 *
 * State
 *
 */
export type UserState = UserLoginResponse;

const state = (): UserState => ({
  token: '',
  user: {
    userInfo: {
      companyId: '',
      email: '',
    },
    username: '',
  },
});


/**
 *
 * Mutations
 *
 */
const mutations: MutationTree<UserState> = {
  reset(state) {
    axios.defaults.headers.common = {
      Authorization: '',
      'Content-Type': 'application/json',
    };

    deleteFromLocalStorage();

    state.token = '';
    state.user = {
      userInfo: {
        companyId: '',
        email: '',
      },
      username: '',
    };
  },

  setValues(state, value: UserLoginResponse) {
    axios.defaults.headers.common = {
      Authorization: `Bearer ${value.token}`,
      'Content-Type': 'application/json',
    };

    saveToLocalStorage(value);

    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        state[key] = value[key];
      }
    }
  },
};


/**
 *
 * Actions
 *
 */
interface UserActions {
  changePassword: (credentials: UserChangePasswordCredentials) => Promise <any>;
  logIn: (credentials: UserLoginCredentials) => Promise <any>;
  logOut: () => void;
  refreshToken: () => Promise <any>;
  requestResetCode: (username: string) => Promise <any>;
  resendTempPassword: (username: string) => Promise <any>;
  resetPassword: (credentials: UserResetPasswordCredentials) => Promise <any>;
  restoreSession: () => Promise<any>;
  setInitialPassword: (credentials: UserChangePasswordCredentials) => Promise <any>;
  validate: () => Promise <any>;
}

const actions: ActionTree<UserState, RootState> = {
  async changePassword(store, credentials: UserChangePasswordCredentials) {
    const response = (await axios.post('changePassword', credentials)).data;
    store.commit('setValues', response);
  },

  async logIn(store, credentials: UserLoginCredentials) {
    const response = (await axios.post <UserLoginResponse>('login', credentials)).data;
    return store.commit('setValues', response);
  },

  async refreshToken(store) {
    try {
      // Initial load -- try pulling token from local storage.
      const settings = loadFromLocalStorage();
      if (settings && !store.state.token) {
        store.commit('setValues', settings);
      }

      // Check if session is still valid.
      const token = (await axios.post('refreshToken')).data;

      // Save token to local storage (and $axios defaults).
      if (settings) {
        const newSettings = { ...settings, token };
        saveToLocalStorage(newSettings);
        store.commit('setValues', newSettings);
        return Promise.resolve();
      }
    }
    catch (error) {
      console.warn(error);
    }

    return Promise.reject();
  },

  async requestResetCode(store, username: string) {
    await axios.post('verificationCode', { username });
  },

  async resendTempPassword(store, username: string) {
    await axios.post('resendTempPassword', { username });
  },

  async resetPassword(store, credentials: UserResetPasswordCredentials) {
    await axios.post('confirmPasswordReset', credentials);
  },

  async restoreSession(store) {
    const settings = loadFromLocalStorage();
    if (settings) {
      store.commit('setValues', settings);
    }
    else {
      store.commit('reset');
    }
  },

  async setInitialPassword(store, credentials: UserChangePasswordCredentials) {
    const { token, ...user } = (await axios.post <UserLoginResponse>('forceChangePassword', credentials)).data;
    store.commit('setValues', { user, token });
  },

  validate(/*store*/) {
    // try {
      // Initial load -- try pulling token from local storage.
      // const settings = loadFromLocalStorage();
      // if (settings && !store.state.token) {
      //   store.commit('setValues', settings);
      // }

      // Check if session is still valid.
      // const token = (await axios.post('validate')).data;
      //
      // // Save token to local storage (and $axios defaults).
      // if (settings) {
      //   const newSettings = { ...settings, token };
      //   saveToLocalStorage(newSettings);
      //   store.commit('setValues', newSettings);
      //   return Promise.resolve();
      // }
    // }
    // catch (error) {
    //   console.warn(error);
    // }
    //
    // return Promise.reject();
    return axios.post('validate');
  },
};



export default {
  namespaced: true,
  actions,
  mutations,
  state,
} as Module<UserState, RootState>;


// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace UserStore {
  export const Bind = namespace('user');
  export type State <TProp extends keyof UserState> = UserState[TProp];
  export type Action <TProp extends keyof UserActions> = UserActions[TProp];
}
